/**
 * COISetupModal Component
 *
 * Modal for requesting a Certificate of Insurance (COI).
 * Collects business name and address for the certificate holder.
 */

import { useEffect, useState } from 'react';
import { useGetProviderBillingInfoQuery } from '@features/user';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

interface COISetupModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: COIFormData) => Promise<void>;
}

export interface COIFormData {
  businessName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface FormState {
  businessName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export function COISetupModal({ open, onClose, onSubmit }: COISetupModalProps) {
  const { data: billingInfo } = useGetProviderBillingInfoQuery(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const [form, setForm] = useState<FormState>({
    businessName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });

  // Prefill form when modal opens with provider billing info (address only, not business name)
  useEffect(() => {
    if (open) {
      const providerInfo = billingInfo?.provider_billing_info;
      setForm({
        businessName: '', // No prefill - user must enter
        addressLine1: providerInfo?.facility_address || '',
        addressLine2: providerInfo?.facility_address2 || '',
        city: providerInfo?.city || '',
        state: providerInfo?.state || '',
        zip: providerInfo?.zip || '',
        country: 'US',
      });
      setError(null);
      setShowSuccess(false);
    }
  }, [open, billingInfo?.provider_billing_info]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.businessName.trim()) {
      setError('Business name is required');
      return;
    }
    if (!form.addressLine1.trim()) {
      setError('Address is required');
      return;
    }
    if (!form.city.trim()) {
      setError('City is required');
      return;
    }
    if (!form.state.trim()) {
      setError('State is required');
      return;
    }
    if (!form.zip.trim()) {
      setError('ZIP code is required');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        businessName: form.businessName.trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        country: form.country.trim(),
      });
      setShowSuccess(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit COI request. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Certificate of Insurance</DialogTitle>
      <DialogContent>
        {showSuccess ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your Certificate of Insurance request has been submitted successfully.
            </Alert>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Active in 2-3 business days.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Our team will prepare your Certificate of Insurance naming your organization as
              certificate holder. You will receive an email notification once it&apos;s ready.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Get priority access to a Certificate of Insurance naming your organization as
              certificate holder, plus supporting policy documentation for procurement. Our coverage
              includes cyber liability, E&O, and general liability (with additional protections
              available upon request).
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label="Business Name"
              fullWidth
              required
              value={form.businessName}
              onChange={handleChange('businessName')}
              disabled={isSubmitting}
              placeholder="Enter your business or organization name"
              helperText="The organization to be named as certificate holder"
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 1"
                  fullWidth
                  required
                  value={form.addressLine1}
                  onChange={handleChange('addressLine1')}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Address Line 2"
                  fullWidth
                  value={form.addressLine2}
                  onChange={handleChange('addressLine2')}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  required
                  value={form.city}
                  onChange={handleChange('city')}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="State"
                  fullWidth
                  required
                  value={form.state}
                  onChange={handleChange('state')}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="ZIP Code"
                  fullWidth
                  required
                  value={form.zip}
                  onChange={handleChange('zip')}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Country"
                  fullWidth
                  value={form.country}
                  onChange={handleChange('country')}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {showSuccess ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isSubmitting ? 'Submitting...' : 'Request Certificate'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
